class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('.cart-notification__close, button[type="button"][aria-label*="Fermer"], button[type="button"][aria-label*="close"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.attachItemHandlers();
    this.attachDiscountHandlers();
    this.applyEmptyState();
  }

  open() {
    this.classList.add('is-open');
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        if (typeof trapFocus === 'function') trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      document.body.addEventListener('click', this.onBodyClick);
    });
  }

  close() {
    this.classList.remove('is-open');
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    document.body.style.overflow = '';

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      if (!el || !parsedState.sections || !parsedState.sections[section.id]) return;
      el.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (this.header) this.header.reveal();
    this.attachItemHandlers();
    this.attachDiscountHandlers();
    this.applyEmptyState();
    this.open();
  }

  refreshSections() {
    const sectionIds = this.getSectionsToRender().map((s) => s.id).join(',');
    return fetch(`${window.location.pathname}?sections=${sectionIds}`, {
      credentials: 'same-origin',
    })
      .then((res) => res.json())
      .then((sections) => {
        this.getSectionsToRender().forEach((section) => {
          const el = document.getElementById(section.id);
          if (!el || !sections[section.id]) return;
          el.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
        });
        this.attachItemHandlers();
        this.attachDiscountHandlers();
        this.applyEmptyState();
      });
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product' },
      { id: 'cart-notification-summary' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = parsed.querySelector(selector);
    return target ? target.innerHTML : '';
  }

  applyEmptyState() {
    const itemsContainer = document.getElementById('cart-notification-product');
    const isEmpty = !itemsContainer || itemsContainer.children.length === 0;
    this.querySelectorAll('[data-cart-empty-hide]').forEach((el) => {
      el.style.display = isEmpty ? 'none' : '';
    });
    this.querySelectorAll('[data-cart-empty-show]').forEach((el) => {
      el.hidden = !isEmpty;
    });
  }

  attachItemHandlers() {
    const itemsContainer = document.getElementById('cart-notification-product');
    if (!itemsContainer) return;

    itemsContainer.querySelectorAll('[data-cart-qty-decrease]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.cartQtyDecrease;
        const valueEl = itemsContainer.querySelector(`[data-cart-qty-value="${CSS.escape(key)}"]`);
        const current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
        if (current > 1) this.updateItemQuantity(key, current - 1);
      });
    });

    itemsContainer.querySelectorAll('[data-cart-qty-increase]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.cartQtyIncrease;
        const valueEl = itemsContainer.querySelector(`[data-cart-qty-value="${CSS.escape(key)}"]`);
        const current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
        this.updateItemQuantity(key, current + 1);
      });
    });

    itemsContainer.querySelectorAll('[data-cart-item-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.cartItemRemove;
        this.updateItemQuantity(key, 0);
      });
    });
  }

  updateItemQuantity(key, quantity) {
    const itemRow = document.getElementById(`cart-notification-product-${key}`);
    if (itemRow) itemRow.classList.add('cart-notification__item--loading');

    const body = JSON.stringify({
      id: key,
      quantity: quantity,
      sections: this.getSectionsToRender().map((s) => s.id).join(','),
      sections_url: window.location.pathname,
    });

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body,
      credentials: 'same-origin',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.sections) {
          this.getSectionsToRender().forEach((section) => {
            const el = document.getElementById(section.id);
            if (!el || !data.sections[section.id]) return;
            el.innerHTML = this.getSectionInnerHTML(data.sections[section.id], section.selector);
          });
        }
        this.attachItemHandlers();
        this.attachDiscountHandlers();
        this.applyEmptyState();
      })
      .catch((e) => {
        console.error(e);
        if (itemRow) itemRow.classList.remove('cart-notification__item--loading');
      });
  }

  attachDiscountHandlers() {
    const form = this.querySelector('[data-cart-discount-form]');
    const errorEl = this.querySelector('[data-cart-discount-error]');
    if (form) {
      form.addEventListener('submit', (evt) => {
        evt.preventDefault();
        const input = form.querySelector('[data-cart-discount-input]');
        if (!input) return;
        const code = input.value.trim();
        if (!code) return;

        if (errorEl) {
          errorEl.textContent = '';
          errorEl.removeAttribute('data-visible');
        }

        form.querySelector('.cart-notification__discount-submit').setAttribute('disabled', 'disabled');

        fetch(`/discount/${encodeURIComponent(code)}`, {
          method: 'GET',
          credentials: 'same-origin',
          redirect: 'follow',
        })
          .then(() => this.refreshSections())
          .then(() => {
            const stillForm = this.querySelector('[data-cart-discount-form]');
            const newErrorEl = this.querySelector('[data-cart-discount-error]');
            if (stillForm) {
              if (newErrorEl) {
                newErrorEl.textContent = 'Code invalide ou non applicable.';
                newErrorEl.setAttribute('data-visible', 'true');
              }
            }
          })
          .catch((e) => {
            console.error(e);
            if (errorEl) {
              errorEl.textContent = 'Erreur. Réessaie.';
              errorEl.setAttribute('data-visible', 'true');
            }
          });
      });
    }

    this.querySelectorAll('[data-cart-discount-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        fetch('/discount/', { method: 'GET', credentials: 'same-origin' })
          .then(() => this.refreshSections())
          .catch((e) => console.error(e));
      });
    });
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (!this.classList.contains('is-open')) return;
    if (this.notification.contains(target)) return;
    if (target.closest('cart-notification')) return;
    const disclosure = target.closest('details-disclosure, header-menu');
    this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
    this.close();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
